import React, { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, ArrowRight, Pencil, Trash2, Clock, User as UserIcon, ChevronLeft, ChevronRight } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { Avatar, Badge, Card } from "@/components/ui-em";

export default function ArticleView() {
  const { id } = useParams();
  const [article, setArticle] = useState(null);
  const [siblings, setSiblings] = useState({ prev: null, next: null });
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    setLoading(true);
    setArticle(null);
    setSiblings({ prev: null, next: null });
    setRelated([]);
    api
      .get(`/kb/articles/${id}`)
      .then((r) => {
        setArticle(r.data);
        return Promise.all([
          api.get(`/kb/articles/${id}/siblings`).catch(() => ({ data: { prev: null, next: null } })),
          api.get(`/kb/articles/${id}/related`).catch(() => ({ data: [] })),
        ]);
      })
      .then((results) => {
        if (!results) return;
        const [s, r] = results;
        setSiblings(s.data || { prev: null, next: null });
        setRelated(r.data || []);
      })
      .catch(() => setArticle(false))
      .finally(() => setLoading(false));
  }, [id]);

  const remove = async () => {
    if (!window.confirm("Delete this article?")) return;
    try {
      await api.delete(`/kb/articles/${id}`);
      toast.success("Article deleted");
      navigate("/knowledge-base");
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  if (loading) return <div className="text-zinc-500 text-sm">Loading...</div>;
  if (!article)
    return (
      <div className="text-center py-20">
        <h2 className="font-display text-2xl mb-2">Article not found</h2>
        <Link to="/knowledge-base" className="text-gold hover:underline">Back to Knowledge Base</Link>
      </div>
    );

  return (
    <div className="max-w-3xl mx-auto">
      <Link
        to="/knowledge-base"
        className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-white mb-8 uppercase tracking-widest"
        style={{ transitionProperty: "color", transitionDuration: "180ms" }}
      >
        <ArrowLeft className="w-3.5 h-3.5" /> Knowledge Base
      </Link>

      <div className="flex items-center gap-3 text-[10px] uppercase tracking-[0.24em] mb-6">
        <span className="text-gold font-semibold">{article.category}</span>
        <span className="text-zinc-700">·</span>
        <span className="text-zinc-500">Updated {new Date(article.updated_at).toLocaleDateString()}</span>
        {article.status === "draft" && (
          <>
            <span className="text-zinc-700">·</span>
            <Badge variant="warn" size="xs" uppercase>Draft</Badge>
          </>
        )}
      </div>

      <h1 className="font-display text-4xl sm:text-5xl font-light tracking-tight leading-[1.1] text-white mb-8">
        {article.title}
      </h1>

      <div className="flex items-center justify-between border-y border-white/5 py-5 mb-12">
        <div className="flex items-center gap-4">
          <Avatar name={article.author} size="md" />
          <div>
            <div className="text-sm text-white">{article.author}</div>
            <div className="text-xs text-zinc-500 flex items-center gap-2 mt-0.5">
              <span>{new Date(article.created_at).toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
              {article.reading_time > 0 && (
                <>
                  <span>·</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {article.reading_time} min read</span>
                </>
              )}
            </div>
          </div>
        </div>
        {user?.role === "admin" && (
          <div className="flex gap-2">
            <Link
              to={`/knowledge-base/${id}/edit`}
              data-testid="edit-article-btn"
              className="bg-white/5 hover:bg-white/10 text-white text-xs rounded-lg px-3 py-2 flex items-center gap-1.5"
              style={{ transitionProperty: "background-color", transitionDuration: "180ms" }}
            >
              <Pencil className="w-3 h-3" /> Edit
            </Link>
            <button
              onClick={remove}
              data-testid="delete-article-btn"
              className="bg-white/5 hover:bg-red-900/40 hover:text-red-200 text-zinc-400 text-xs rounded-lg px-3 py-2 flex items-center gap-1.5"
              style={{ transitionProperty: "background-color, color", transitionDuration: "180ms" }}
            >
              <Trash2 className="w-3 h-3" /> Delete
            </button>
          </div>
        )}
      </div>

      <article className="em-prose" dangerouslySetInnerHTML={{ __html: article.content_html }} />

      {article.tags?.length > 0 && (
        <div className="mt-12 pt-6 border-t border-white/5 flex flex-wrap gap-2">
          {article.tags.map((t) => (
            <Badge key={t} variant="default" size="md">#{t}</Badge>
          ))}
        </div>
      )}

      {/* Prev / Next */}
      {(siblings.prev || siblings.next) && (
        <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-4">
          {siblings.prev ? (
            <Link to={`/knowledge-base/${siblings.prev.id}`} className="em-card em-card-hover p-6 flex flex-col group">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-3">
                <ChevronLeft className="w-3 h-3" /> Previous
              </div>
              <div className="text-white group-hover:text-gold" style={{ transitionProperty: "color", transitionDuration: "160ms" }}>{siblings.prev.title}</div>
            </Link>
          ) : <div />}
          {siblings.next ? (
            <Link to={`/knowledge-base/${siblings.next.id}`} className="em-card em-card-hover p-6 flex flex-col items-end text-right group">
              <div className="flex items-center gap-2 text-[10px] uppercase tracking-[0.24em] text-zinc-500 mb-3">
                Next <ChevronRight className="w-3 h-3" />
              </div>
              <div className="text-white group-hover:text-gold" style={{ transitionProperty: "color", transitionDuration: "160ms" }}>{siblings.next.title}</div>
            </Link>
          ) : <div />}
        </div>
      )}

      {/* Related */}
      {related.length > 0 && (
        <div className="mt-16">
          <p className="text-[10px] uppercase tracking-[0.24em] text-zinc-500 font-semibold mb-4">Related articles</p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {related.map((a) => (
              <Link key={a.id} to={`/knowledge-base/${a.id}`} className="em-card em-card-hover p-5 group">
                <div className="text-[10px] uppercase tracking-widest text-gold mb-3">{a.category}</div>
                <div className="text-sm text-white group-hover:text-gold leading-snug" style={{ transitionProperty: "color", transitionDuration: "160ms" }}>{a.title}</div>
                {a.reading_time > 0 && <div className="text-xs text-zinc-500 mt-3">{a.reading_time} min read</div>}
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
